import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { deptLabel } from "../../lib/labels";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Check } from "lucide-react";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import Input, { Select } from "../../components/system/Input";

const STATUS_FILTERS = [
  { value: "active",   label: "Active" },
  { value: "disabled", label: "Disabled" },
  { value: "all",      label: "All" },
];

const STATUS_STORAGE_KEY = "shelflife.admin.users.status_filter";

// ============================================================================
// Role model (mirrors backend)
// ----------------------------------------------------------------------------
// Backend persists 4 role values:
//   admin              — HQ, no store, no department.
//   manager            — Store Manager: store required, dept null.
//   department_manager — Dept Manager:  store + dept BOTH required.
//   employee           — store + dept BOTH required.
//
// The UI labels Store Manager / Department Manager explicitly so admins
// never have to guess what "manager" means. Adding "department_manager"
// to a role dropdown is the explicit promotion path; the legacy
// "manager" value remains for existing Store Manager rows.
// ============================================================================
const ROLE_LABELS = {
  admin:               "Admin",
  manager:             "Store Manager",
  department_manager:  "Department Manager",
  employee:            "Employee",
};

const ROLE_REQUIRES_STORE = new Set(["manager", "department_manager", "employee"]);
const ROLE_REQUIRES_DEPT  = new Set(["department_manager", "employee"]);

function roleNeeds(role, field) {
  if (field === "store_id")     return ROLE_REQUIRES_STORE.has(role);
  if (field === "department_id") return ROLE_REQUIRES_DEPT.has(role);
  return false;
}

/**
 * Decide what fields a role change needs from the current row.
 * Returns ``null`` when the current row already satisfies the target
 * role's contract and the PATCH can be sent immediately; otherwise
 * returns the list of missing field names so the UI can collect them
 * before saving.
 */
function fieldsMissingForRole(role, currentRow) {
  const missing = [];
  if (roleNeeds(role, "store_id") && !currentRow.store_id) missing.push("store_id");
  if (roleNeeds(role, "department_id") && !currentRow.department_id) missing.push("department_id");
  return missing;
}

export default function UsersPage() {
  const { user: me } = useAuth() || {};
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [stores, setStores] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "employee", department_id: "", store_id: "" });
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Per-row pending role-change state. When the admin picks a role for
  // a row that needs fields the row doesn't have, we DO NOT fire-and-
  // forget the PATCH (that's how Manager → Employee blew up with
  // "Employee must have a department"). Instead, we stash the
  // target role + a fresh draft of store/department here, render
  // inline pickers, and only POST when the admin clicks Save.
  //
  // Shape: { [userId]: { role, store_id, department_id } | undefined }
  const [pending, setPending] = useState({});

  // Status filter — persists across reloads via localStorage so the
  // admin's preference survives navigation. Default = "active" per
  // the cleanup spec (disabled demo users should not clutter the
  // main view).
  const [statusFilter, setStatusFilter] = useState(() => {
    try {
      const v = window.localStorage.getItem(STATUS_STORAGE_KEY);
      if (v && STATUS_FILTERS.some((s) => s.value === v)) return v;
    } catch (_) { /* ignore */ }
    return "active";
  });
  useEffect(() => {
    try { window.localStorage.setItem(STATUS_STORAGE_KEY, statusFilter); } catch (_) { /* ignore */ }
  }, [statusFilter]);

  const load = useCallback(async () => {
    try {
      const [u, d, s] = await Promise.all([
        api.get("/users"),
        api.get("/departments"),
        api.get("/stores"),
      ]);
      setUsers(u.data); setDepts(d.data); setStores(s.data);
      // For managers, pre-fill (and effectively lock) the store to their own.
      const defaultStore = me?.role === "manager"
        ? (me.store_id || s.data[0]?.id || "")
        : (s.data[0]?.id || "");
      setForm((f) => ({
        ...f,
        department_id: f.department_id || d.data[0]?.id || "",
        store_id: f.store_id || defaultStore,
      }));
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  }, [me?.role, me?.store_id]);
  useEffect(() => { load(); }, [load]);

  // Derived: counts for the status pills, and the rows to render.
  const counts = useMemo(() => {
    const active = users.filter((u) => u.active).length;
    return {
      active,
      disabled: users.length - active,
      all: users.length,
    };
  }, [users]);

  const visibleUsers = useMemo(() => {
    if (statusFilter === "active")   return users.filter((u) => u.active);
    if (statusFilter === "disabled") return users.filter((u) => !u.active);
    return users;
  }, [users, statusFilter]);

  // Safeguard helper: count active admins.
  const activeAdminCount = useMemo(
    () => users.filter((u) => u.active && u.role === "admin").length,
    [users],
  );

  const create = async () => {
    // Frontend role-scope gate. Mirrors backend rules so we never POST
    // a payload we know will be rejected.
    if (form.role === "manager") {
      if (!form.store_id) {
        toast.error("Store Manager must have a store");
        return;
      }
    } else if (form.role === "department_manager") {
      if (!form.store_id) {
        toast.error("Department Manager must have a store");
        return;
      }
      if (!form.department_id) {
        toast.error("Department Manager must have a department");
        return;
      }
    } else if (form.role === "employee") {
      if (!form.department_id) {
        toast.error("Employee must have a department");
        return;
      }
      if (!form.store_id) {
        toast.error("Employee must have a store");
        return;
      }
    }
    setSubmitting(true);
    try {
      await api.post("/users", {
        email: form.email,
        password: form.password,
        name: form.name,
        role: form.role,
        department_id: ROLE_REQUIRES_DEPT.has(form.role) ? form.department_id : null,
        store_id:      ROLE_REQUIRES_STORE.has(form.role) ? form.store_id : null,
      });
      toast.success("User created");
      setShow(false);
      setForm({ email: "", password: "", name: "", role: "employee", department_id: depts[0]?.id || "", store_id: stores[0]?.id || "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); } finally { setSubmitting(false); }
  };

  // -------- Safe role-switching ----------------------------------------
  // A role switch is *safe* when the current row already has every field
  // the new role requires. In that case the PATCH fires immediately and
  // any now-irrelevant fields are explicitly cleared (so a row migrating
  // employee → admin is fully detached from its previous scope).
  //
  // When the new role REQUIRES additional fields the row is missing
  // (e.g. Manager → Employee needs department_id), the row enters
  // pending mode: inline pickers are rendered, the dropdown shows the
  // pending target role, and a single compound PATCH is sent only on
  // explicit Save.
  const patchUser = async (u, field, value, label) => {
    try {
      const body = { [field]: value || null };
      await api.patch(`/users/${u.id}`, body);
      toast.success(label);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const setRoleFor = (u, role) => {
    // Build a hypothetical post-patch row: the role change clears
    // fields the new role does NOT use, but PRESERVES fields it still
    // needs.
    const projectedRow = {
      store_id:      ROLE_REQUIRES_STORE.has(role) ? u.store_id      : null,
      department_id: ROLE_REQUIRES_DEPT.has(role)  ? u.department_id : null,
    };
    const missing = fieldsMissingForRole(role, projectedRow);

    if (missing.length === 0) {
      // Safe immediate save.
      const body = { role };
      // Always send the cleared fields explicitly so backend sees them.
      if (!ROLE_REQUIRES_STORE.has(role)) body.store_id = null;
      if (!ROLE_REQUIRES_DEPT.has(role))  body.department_id = null;
      // For Store Manager promotion from Employee: preserve store_id;
      // body.role is already in the body so backend will accept the
      // single-field patch.
      (async () => {
        try {
          await api.patch(`/users/${u.id}`, body);
          toast.success(`Set to ${ROLE_LABELS[role] || role}`);
          load();
        } catch (e) { toast.error(formatApiErrorDetail(e)); }
      })();
      return;
    }

    // Pending mode — collect missing fields before saving.
    setPending((prev) => ({
      ...prev,
      [u.id]: {
        role,
        store_id:      projectedRow.store_id      ?? (stores[0]?.id || ""),
        department_id: projectedRow.department_id ?? (depts[0]?.id  || ""),
      },
    }));
  };

  const updatePending = (uid, field, value) => {
    setPending((prev) => ({ ...prev, [uid]: { ...(prev[uid] || {}), [field]: value } }));
  };

  const cancelPending = (uid) => {
    setPending((prev) => {
      const { [uid]: _drop, ...rest } = prev;
      return rest;
    });
  };

  const savePending = async (u) => {
    const p = pending[u.id];
    if (!p) return;
    // Validate locally so the user sees a precise message before we POST.
    if (ROLE_REQUIRES_STORE.has(p.role) && !p.store_id) {
      toast.error(`${ROLE_LABELS[p.role]} must have a store.`);
      return;
    }
    if (ROLE_REQUIRES_DEPT.has(p.role) && !p.department_id) {
      toast.error(`${ROLE_LABELS[p.role]} must have a department.`);
      return;
    }
    const body = {
      role: p.role,
      store_id:      ROLE_REQUIRES_STORE.has(p.role) ? p.store_id      : null,
      department_id: ROLE_REQUIRES_DEPT.has(p.role)  ? p.department_id : null,
    };
    try {
      await api.patch(`/users/${u.id}`, body);
      toast.success(`Set to ${ROLE_LABELS[p.role] || p.role}`);
      cancelPending(u.id);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  };
  const setDeptFor = (u, department_id) =>
    patchUser(u, "department_id", department_id, department_id ? "Department assigned" : "Department cleared");
  const setStoreFor = (u, store_id) =>
    patchUser(u, "store_id", store_id, store_id ? "Store assigned" : "Store cleared");

  const performToggle = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const toggleActive = async (u) => {
    // Disable flow guards
    if (u.active) {
      // Safeguard #1: refuse to disable the last active admin.
      if (u.role === "admin" && activeAdminCount <= 1) {
        toast.error("Cannot disable the last active admin");
        return;
      }
    }
    await performToggle(u);
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  return (
    <div data-testid="admin-users-page">
      <PageHeader
        eyebrow="Team"
        title={me?.role === "manager" ? "Employees" : "Users"}
        subtitle={
          <span data-testid="users-count-summary">
            <span className="font-mono text-ink">{visibleUsers.length}</span>
            {" "}of <span className="font-mono">{counts.all}</span>{" "}
            {statusFilter === "all" ? "users" : `${statusFilter} users`}
          </span>
        }
        actions={
          <Button tier="primary" onClick={() => setShow(true)} data-testid="new-user-button">
            <Plus size={16} /> New {me?.role === "manager" ? "employee" : "user"}
          </Button>
        }
      />

      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="users-status-filter" role="tablist" aria-label="User status filter">
        {STATUS_FILTERS.map((s) => {
          const active = statusFilter === s.value;
          const count = counts[s.value] ?? 0;
          return (
            <button
              key={s.value}
              onClick={() => setStatusFilter(s.value)}
              className={[
                "h-9 px-3 rounded-chip font-mono text-[11px] font-bold uppercase tracking-wider transition-all duration-150 active:scale-[0.98] inline-flex items-center gap-2",
                active
                  ? "bg-paper-sunk text-ink shadow-press"
                  : "bg-paper-2 text-ink-2 shadow-rest hover:shadow-raised",
              ].join(" ")}
              data-testid={`users-status-filter-${s.value}`}
              role="tab"
              aria-selected={active}
            >
              {s.label}
              <span className={`text-[10px] ${active ? "text-ink-3" : "text-ink-3"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <Card variant="raised" className="overflow-hidden">
        <div className="hidden md:grid grid-cols-[1.6fr_0.9fr_1.1fr_1.1fr_0.8fr_auto] gap-4 px-4 py-3 bg-paper-sunk border-b border-border/60 data-sku">
          <div>Email · Name</div>
          <div>Role</div>
          <div>Department</div>
          <div>Store</div>
          <div>Status</div>
          <div></div>
        </div>

        {visibleUsers.length === 0 ? (
          <div className="p-6 text-sm text-ink/50" data-testid="users-empty">
            No {statusFilter === "all" ? "" : `${statusFilter} `}users to show.
          </div>
        ) : visibleUsers.map((u) => (
          <div
            key={u.id}
            className={`md:grid grid-cols-[1.6fr_0.9fr_1.1fr_1.1fr_0.8fr_auto] gap-4 px-4 py-4 border-b border-border items-center ${u.active ? "" : "opacity-70"}`}
            data-testid={`user-row-${u.id}`}
          >
            <div>
              <div className="font-display font-semibold">{u.email}{me && u.id === me.id && <span className="ml-2 data-sku text-[10px] text-ink/40">YOU</span>}</div>
              <div className="data-sku">{u.name}</div>
            </div>

            <div>
              {me?.role === "manager" ? (
                <span
                  className="font-mono text-xs text-ink/60 uppercase select-none"
                  data-testid={`user-role-${u.id}-locked`}
                  title="Managers can only manage employees"
                >
                  {ROLE_LABELS[u.role] || u.role}
                </span>
              ) : (
                <select
                  value={pending[u.id]?.role ?? u.role}
                  onChange={(e) => setRoleFor(u, e.target.value)}
                  className="h-9 px-2 rounded-button bg-paper-2 border border-border/60 shadow-rest font-mono text-xs w-full max-w-[170px] uppercase focus:shadow-raised focus:outline-none transition-shadow"
                  data-testid={`user-role-${u.id}`}
                >
                  <option value="employee">{ROLE_LABELS.employee}</option>
                  <option value="manager">{ROLE_LABELS.manager}</option>
                  <option value="department_manager">{ROLE_LABELS.department_manager}</option>
                  <option value="admin">{ROLE_LABELS.admin}</option>
                </select>
              )}
            </div>

            <div>
              {(() => {
                const effectiveRole = pending[u.id]?.role ?? u.role;
                if (effectiveRole === "admin") {
                  return (
                    <span
                      className="font-mono text-xs text-ink/40 select-none"
                      data-testid={`user-department-${u.id}-na`}
                      title="Admins are not scoped to a department"
                    >
                      — admin —
                    </span>
                  );
                }
                if (effectiveRole === "manager") {
                  return (
                    <span
                      className="font-mono text-xs text-ink/40 select-none"
                      data-testid={`user-department-${u.id}-all`}
                      title="Store Managers oversee all departments in their store"
                    >
                      — all in store —
                    </span>
                  );
                }
                // employee or department_manager → show picker.
                const value = pending[u.id]
                  ? (pending[u.id].department_id || "")
                  : (u.department_id || "");
                const onChange = (e) => {
                  if (pending[u.id]) {
                    updatePending(u.id, "department_id", e.target.value);
                  } else {
                    setDeptFor(u, e.target.value);
                  }
                };
                return (
                  <select
                    value={value}
                    onChange={onChange}
                    className={`h-9 px-2 rounded-button bg-paper-2 border ${pending[u.id] ? "border-status-attention" : "border-border/60"} shadow-rest font-mono text-xs w-full max-w-[180px] uppercase focus:shadow-raised focus:outline-none transition-shadow`}
                    data-testid={`user-department-${u.id}`}
                    aria-invalid={pending[u.id] && !value ? "true" : "false"}
                  >
                    <option value="">— Select department —</option>
                    {depts.map((d) => (
                      <option key={d.id} value={d.id}>{deptLabel(d.name)}</option>
                    ))}
                  </select>
                );
              })()}
            </div>

            <div>
              {(() => {
                const effectiveRole = pending[u.id]?.role ?? u.role;
                if (effectiveRole === "admin") {
                  return (
                    <span
                      className="inline-flex items-center"
                      data-testid={`user-store-${u.id}-main-office`}
                      title="Admins are HQ users — they see all stores"
                    >
                      <span className="inline-flex items-center gap-2 h-7 px-3 rounded-chip font-mono text-[11px] uppercase tracking-wider bg-paper-sunk text-ink/80 border border-border/60">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand" aria-hidden="true"></span>
                        <span className="font-bold">HQ</span>
                      </span>
                    </span>
                  );
                }
                const value = pending[u.id]
                  ? (pending[u.id].store_id || "")
                  : (u.store_id || "");
                const onChange = (e) => {
                  if (pending[u.id]) {
                    updatePending(u.id, "store_id", e.target.value);
                  } else {
                    setStoreFor(u, e.target.value);
                  }
                };
                return (
                  <select
                    value={value}
                    onChange={onChange}
                    className={`h-9 px-2 rounded-button bg-paper-2 border ${pending[u.id] ? "border-status-attention" : "border-border/60"} shadow-rest font-mono text-xs w-full max-w-[180px] focus:shadow-raised focus:outline-none transition-shadow`}
                    data-testid={`user-store-${u.id}`}
                    aria-invalid={pending[u.id] && !value ? "true" : "false"}
                  >
                    <option value="">— Select store —</option>
                    {stores.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ""}</option>
                    ))}
                  </select>
                );
              })()}
            </div>

            <div>
              <span className={`badge-pill ${u.active ? "bg-status-fresh-bg text-status-fresh" : "bg-status-expired-bg text-status-expired"}`}>
                {u.active ? "Active" : "Disabled"}
              </span>
            </div>

            <div className="flex gap-2 justify-end mt-2 md:mt-0 items-center flex-wrap">
              {pending[u.id] && (
                <>
                  <Button
                    tier="primary"
                    size="sm"
                    onClick={() => savePending(u)}
                    data-testid={`save-role-${u.id}`}
                  >
                    <Check size={12} strokeWidth={2.5} /> Save
                  </Button>
                  <Button
                    tier="secondary"
                    size="sm"
                    onClick={() => cancelPending(u.id)}
                    data-testid={`cancel-role-${u.id}`}
                  >
                    Cancel
                  </Button>
                </>
              )}
              {me && u.id === me.id ? (
                <span
                  className="font-mono text-[10px] uppercase tracking-wider text-ink-3 italic max-w-[220px] text-right leading-tight"
                  data-testid={`self-row-note-${u.id}`}
                  title="You cannot disable or delete the account you are signed in with"
                >
                  Current account cannot be disabled or deleted while signed in.
                </span>
              ) : (
                <>
                  <Button
                    tier="secondary"
                    size="sm"
                    onClick={() => toggleActive(u)}
                    data-testid={u.active ? `disable-user-${u.id}` : `enable-user-${u.id}`}
                  >
                    {u.active ? "Disable" : "Enable"}
                  </Button>
                  {me?.role === "admin" && (
                    <Button
                      tier="secondary"
                      size="sm"
                      onClick={() => remove(u)}
                      className="text-status-urgent"
                      data-testid={`delete-user-${u.id}`}
                    >
                      Delete
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </Card>

      {show && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 px-3" data-testid="new-user-dialog">
          <Card variant="raised" className="p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-black text-2xl tracking-tight">New user</h2>
              <button onClick={() => setShow(false)} className="p-2 -mt-2 -mr-2 rounded-button text-ink-2 hover:text-ink"><X size={18}/></button>
            </div>
            <div className="space-y-3">
              <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" />
              <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" />
              <Input placeholder="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" />
              <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role" disabled={me?.role === "manager"}>
                <option value="employee">{ROLE_LABELS.employee}</option>
                {me?.role !== "manager" && <option value="manager">{ROLE_LABELS.manager}</option>}
                {me?.role !== "manager" && <option value="department_manager">{ROLE_LABELS.department_manager}</option>}
                {me?.role !== "manager" && <option value="admin">{ROLE_LABELS.admin}</option>}
              </Select>
              {ROLE_REQUIRES_DEPT.has(form.role) && (
                <Select value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} data-testid="new-user-department">
                  <option value="">— Select department —</option>
                  {depts.map((d) => (<option key={d.id} value={d.id}>{deptLabel(d.name)}</option>))}
                </Select>
              )}
              {form.role === "admin" ? (
                <div
                  className="w-full h-11 px-3 rounded-button bg-paper-sunk flex items-center gap-2 text-xs font-mono text-ink/70"
                  data-testid="new-user-admin-scope-note"
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand" aria-hidden="true"></span>
                  Admin = HQ (no store, no department)
                </div>
              ) : (
                <Select value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })} data-testid="new-user-store">
                  <option value="">— Select store —</option>
                  {stores.map((s) => (<option key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ""}</option>))}
                </Select>
              )}
              {form.role === "manager" && (
                <div
                  className="w-full px-3 py-2 rounded-button bg-paper-sunk flex items-center gap-2 text-xs font-mono text-ink/70"
                  data-testid="new-user-manager-scope-note"
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand" aria-hidden="true"></span>
                  Store Manager = oversees all departments in their store
                </div>
              )}
              {form.role === "department_manager" && (
                <div
                  className="w-full px-3 py-2 rounded-button bg-paper-sunk flex items-center gap-2 text-xs font-mono text-ink/70"
                  data-testid="new-user-dept-manager-scope-note"
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand" aria-hidden="true"></span>
                  Department Manager = manages one department in one store
                </div>
              )}
              <Button tier="primary" className="w-full" onClick={create} disabled={submitting} data-testid="submit-new-user">
                {submitting ? "Creating…" : "Create user"}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
